/**
 * Build-time endpoint configuration.
 *
 * These endpoints used to be module-level constants pointing straight at production
 * (`api.xapp.ai/graphql` in partnerBranding.ts, `form.xapp.ai` in App.tsx), which meant
 * `yarn build` could only ever produce a production bundle - there was no way to build
 * the contact app against dev, and a deploy to a dev bucket still talked to prod.
 *
 * Note this is unrelated to the `environment`/`env` URL parameter, which is stored as
 * `xa_environment` and forwarded to the backend as a request attribute by the chat and
 * form widgets. That switches behaviour *within* an environment; it does not choose
 * which API host the contact app calls.
 */

export interface Endpoints {
    /** GraphQL API used for partner branding lookups. */
    graphqlUrl: string;
    /** Base URL the widget config JSON is fetched from. */
    formBaseUrl: string;
}

export type EnvironmentName = "development" | "production";

/**
 * Production runs on xapp.ai; the dev estate is on **xapp.media**, and the two do not
 * mirror each other's names. `dev-api.xapp.ai` looks like the obvious counterpart to
 * `api.xapp.ai` and appears in stentor-client's `packages/studio/config/accounts.ts`, but
 * it does not resolve - the live dev hosts are `dev-api.xapp.media` (same GraphQL schema
 * as production) and `dev-widget.xapp.media` (serves config.json for dev widget keys,
 * where `form.xapp.ai` returns 404).
 *
 * Verified by DNS and by request against each host; do not infer a hostname here from the
 * production one.
 */
export const ENVIRONMENTS: Record<EnvironmentName, Endpoints> = {
    production: {
        graphqlUrl: "https://api.xapp.ai/graphql",
        formBaseUrl: "https://form.xapp.ai",
    },
    development: {
        graphqlUrl: "https://dev-api.xapp.media/graphql",
        formBaseUrl: "https://dev-widget.xapp.media",
    },
};

type Environment = Record<string, string | undefined>;

/**
 * Reads one build-time variable, tolerating the absence of a `process` global.
 *
 * The getter must spell the access out in full (`process.env.REACT_APP_...`), because a
 * fully-qualified member expression is the form bundlers substitute with a literal. A bare
 * `process.env` reference survives into the published library bundle - rollup's `plugins()`
 * replaces only the MODULE_MODE flag and injects no `process` shim, unlike the UMD
 * target - and then throws `ReferenceError: process is not defined` in any consumer that
 * has no `process` global, such as a Vite app or the raw ESM file. That throw would happen
 * while this module is still evaluating, before any of the app's own code runs.
 *
 * Only a missing global is swallowed; anything else propagates.
 */
export function readOptional(get: () => string | undefined): string | undefined {
    try {
        return get();
    } catch (error) {
        if (error instanceof ReferenceError) {
            return undefined;
        }
        throw error;
    }
}

/** The build environment, or an empty one where `process` does not exist. */
function readBuildEnvironment(): Environment {
    return {
        REACT_APP_XAPP_ENV: readOptional(() => process.env.REACT_APP_XAPP_ENV),
        REACT_APP_GRAPHQL_URL: readOptional(() => process.env.REACT_APP_GRAPHQL_URL),
        REACT_APP_FORM_BASE_URL: readOptional(() => process.env.REACT_APP_FORM_BASE_URL),
    };
}

/**
 * Resolves endpoints from the build environment.
 *
 * `REACT_APP_XAPP_ENV` selects a preset; `REACT_APP_GRAPHQL_URL` and
 * `REACT_APP_FORM_BASE_URL` override individual hosts. An unrecognised environment name
 * throws rather than falling back to production - a typo that silently ships dev traffic
 * to prod is the failure this is meant to prevent.
 */
export function resolveEndpoints(env: Environment = readBuildEnvironment()): Endpoints {
    const name = env.REACT_APP_XAPP_ENV;
    let preset = ENVIRONMENTS.production;

    if (name) {
        const known = ENVIRONMENTS[name as EnvironmentName];
        if (!known) {
            throw new Error(
                `Unknown REACT_APP_XAPP_ENV "${name}". Known environments: ${Object.keys(
                    ENVIRONMENTS
                ).join(", ")}`
            );
        }
        preset = known;
    }

    return {
        graphqlUrl: env.REACT_APP_GRAPHQL_URL || preset.graphqlUrl,
        formBaseUrl: env.REACT_APP_FORM_BASE_URL || preset.formBaseUrl,
    };
}

/** Endpoints for this build, resolved once at module load. */
export const ENDPOINTS: Endpoints = resolveEndpoints();
